/**
 * Recovery codes display — shown once after TOTP enrollment or regeneration.
 * The user must copy/download the codes and confirm they have saved them.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Download, KeyRound } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';

interface RecoveryCodesPanelProps {
  codes: string[];
  /** Called when the user confirms they saved the codes and continues. */
  onDone: () => void;
  /** Label for the continue button (defaults to a generic "Done"). */
  doneLabel?: string;
}

export function RecoveryCodesPanel({ codes, onDone, doneLabel }: RecoveryCodesPanelProps) {
  const { t } = useTranslation('auth');
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const codesText = codes.join('\n');

  const handleCopy = async () => {
    await navigator.clipboard.writeText(codesText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([`${t('mfa.recoveryCodes.fileHeader')}\n\n${codesText}\n`], {
      type: 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'panluma-recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-warning/10">
          <KeyRound className="h-5 w-5 text-warning" />
        </div>
        <div className="space-y-1">
          <h3 className="text-base font-semibold">{t('mfa.recoveryCodes.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('mfa.recoveryCodes.description')}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-muted/40 p-4">
        {codes.map((code) => (
          <code key={code} className="text-center font-mono text-sm tracking-wider text-foreground">
            {code}
          </code>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
          {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
          {copied ? t('mfa.recoveryCodes.copied') : t('mfa.recoveryCodes.copy')}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={handleDownload}>
          <Download className="mr-1.5 h-4 w-4" />
          {t('mfa.recoveryCodes.download')}
        </Button>
      </div>

      <div className="flex items-start gap-2 rounded-lg bg-warning/10 p-3">
        <Checkbox
          id="mfa-recovery-confirmed"
          checked={confirmed}
          onCheckedChange={(v) => setConfirmed(v === true)}
          className="mt-0.5"
        />
        <Label
          htmlFor="mfa-recovery-confirmed"
          className="text-sm font-normal leading-snug text-foreground"
        >
          {t('mfa.recoveryCodes.confirmSaved')}
        </Label>
      </div>

      <Button type="button" className="w-full" disabled={!confirmed} onClick={onDone}>
        {doneLabel ?? t('mfa.recoveryCodes.done')}
      </Button>
    </div>
  );
}
